import React, { useEffect, useState, useCallback, useMemo } from 'react';
import DataTable from '../DataTable.tsx';
import { Button, LinearProgress } from '@mui/material';
import { useSnackbarMessage } from '../SnackMessge.tsx';
import { LovInfoDictionary } from '../../misc/LovInfoDictionary.tsx';
import { MonitoringData } from './MonitoringData.tsx';
import { ResistanceDataPageEdit } from './ResistanceDataPageEdit.tsx';
import { ResistanceDataPageNew } from './ResistanceDataPageNew.tsx';
import * as common from './Common.ts';
import * as lov_common from '../../misc/Lovs.ts';
import { useDependencies } from '../../misc/DependenciesProvider.tsx';
import * as frac_api from "../../api/FracApi.ts";
import * as column_info from '../../misc/Columns.ts';

interface ResistanceDataPageProps {
    is_admin: boolean;
}

export function ResistanceDataPage({ is_admin }: ResistanceDataPageProps) {
    const [showEdit, setShowEdit] = useState<boolean>(false);
    const [editData, setEditData] = useState<MonitoringData | null>(null);
    const [data, setData] = useState<frac_api.ResistanceData[]>([]);
    const [showAdd, setShowAdd] = useState<boolean>(false);
    const [isLoding, setIsLoading] = useState<boolean>(true);
    const [lovs, setLovs] = useState<LovInfoDictionary | null>(null);
    const [columns, setColumns] = useState<column_info.ColumnInfo[] | null>(null);
    const { showMessage } = useSnackbarMessage();
    const { fracAPI } = useDependencies();

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const vocabData = await fetchVocabData();
            setLovs(vocabData.lovs);
            setColumns(vocabData.columns);
            const resistanceData = await fracAPI.getResData();
            setData(resistanceData);
        } catch (error) {
            console.error("Error loading data: ", error);
            showMessage("Error loading data. Please try again.");
        } finally {
            setIsLoading(false);
        }
    }, [fracAPI, showMessage]);


    const fetchVocabData = useCallback(async () => {
        const companiesData = await fracAPI.getCompanyLookupData();
        const countriesData = await fracAPI.getCountryLookupData();
        const moaCodesData = await fracAPI.getMoaCodesLookupData();
        const monitoringIntensityData = await fracAPI.getMonitoringIntensityLookupData();
        const monitoringTypeData = await fracAPI.getMonitoringTypeLookupData();
        const resistanceFrequencyData = await fracAPI.getResistanceFrequencyLookupData();
        const resistanceFactorData = await fracAPI.getResistanceFactorLookupData();
        const resistanceMechanismData = await fracAPI.getResistanceMechanismLookupData();
        const geoDistributionData = await fracAPI.getGeoDistributionLookupData();
        const resistanceRelevanceData = await fracAPI.getResistanceRelevanceLookupData();

        const processedLovs = processLovData({
            companies: companiesData,
            countries: countriesData,
            moaCodes: moaCodesData,
            monitoringType: monitoringTypeData,
            resistanceFrequency: resistanceFrequencyData,
            monitoringIntensity: monitoringIntensityData,
            resistanceFactor: resistanceFactorData,
            resistanceMechanism: resistanceMechanismData,
            geoDistribution: geoDistributionData,
            resistanceRelevance: resistanceRelevanceData,
            api: fracAPI,
        });

        const generatedColumns = await common.experimentAllColumns(
            is_admin,
            setShowEdit,
            setEditData,
            deleteRecord,
            companiesData,
            countriesData,
            resistanceFrequencyData,
            fracAPI
        );


        return {
            lovs: processedLovs,
            columns: generatedColumns,
        };

    }, [fracAPI, is_admin]);


    const processLovData = useCallback(
        ({
            companies,
            countries,
            moaCodes,
            monitoringType,
            resistanceFrequency,
            monitoringIntensity,
            resistanceFactor,
            resistanceMechanism,
            geoDistribution,
            resistanceRelevance,
            api
        }: {
            companies: frac_api.Company[];
            countries: frac_api.Country[];
            moaCodes: frac_api.MoaCode[];
            monitoringType: frac_api.MonitoringType[];
            resistanceFrequency: frac_api.ResistanceFrequency[];
            monitoringIntensity: frac_api.MonitoringIntensity[];
            resistanceFactor: frac_api.ResistanceFactor[];
            resistanceMechanism: frac_api.ResistanceMechanism[];
            geoDistribution: frac_api.GeoDistribution[];
            resistanceRelevance: frac_api.ResistanceRelevance[];
            api: frac_api.FracApi;
        }): LovInfoDictionary => {
            const currentYear = new Date().getFullYear();

            return {
                crops_dynamic: async (value: string) => {
                    const crops = await api.searchCrops(value);
                    return crops.map(lov_common.convertToLabel);
                },
                pests_dynamic: async (value: string) => {
                    const pests = await api.searchPests(value);
                    return pests.map(lov_common.convertToLabel);
                },
                companies: companies.map((company) => ({ label: company.name_code, value: company.name_code })),
                countries: countries.map((country) => ({ label: country.name, value: country.name })),
                moaCodes: moaCodes.map((moa) => ({ label: moa.name, value: moa.name })),
                monitoringType: monitoringType.map((type) => ({ label: type.type, value: type.type })),
                resistanceFrequency: resistanceFrequency.map((frequency) => ({ label: frequency.frequency, value: frequency.frequency })),
                monitoringIntensity: monitoringIntensity.map((intensity) => ({ label: intensity.intensity, value: intensity.intensity })),
                resistanceFactor: resistanceFactor.map((factor) => ({ label: factor.factor, value: factor.factor })),
                resistanceMechanism: resistanceMechanism.map((mechanism) => ({ label: mechanism.mechanism, value: mechanism.mechanism })),
                geoDistribution: geoDistribution.map((distribution) => ({ label: distribution.distribution, value: distribution.distribution })),
                resistanceRelevance: resistanceRelevance.map((relevance) => ({ label: relevance.relevance, value: relevance.relevance })),
                years: Array.from({ length: 100 }, (_, i) => currentYear - i).map((year) => ({ label: year.toString(), value: year.toString() }))
            };
        },
        []
    );


    useEffect(() => {
        fetchData();
    }, [fetchData]);


    const deleteRecord = useCallback(async (id: number) => {
        try {
            if (!window.confirm("Are you sure you want to delete this record?")) {
                return;
            }
            await fracAPI.deleteMonitoringRecord(id);
            await fetchData();
        } catch (error) {
            console.error("Error deleting record: ", error);
            showMessage("Error deleting record. Please try again.");
        }
    }, [fracAPI, showMessage, fetchData]);


    const handleCloseEdit = useCallback(() => {
        setShowEdit(false);
        setEditData(null);
    }, []);

    const handleChangedData = useCallback((data: MonitoringData) => {
        setEditData(data);
    }, []);

    const handleUpdate = useCallback(async (record: frac_api.ResistanceData) => {
        try {
            await fracAPI.updateMonitoringRecord(record);
            await fetchData();
            handleCloseEdit();
        } catch (error) {
            console.error("Error updating record: ", error);
            showMessage("Error updating record. Please try again.");
        }
    }, [fracAPI, showMessage, fetchData, handleCloseEdit]);

    const handleNew = useCallback(async (record: frac_api.ResistanceData) => {
        try {
            await fracAPI.createMonitoringRecord(record);
            await fetchData();
            // setShowAdd(false);
        } catch (error) {
            console.error("Error creating record: ", error);
            showMessage("Error creating record. Please try again.");
        }
    }, [fracAPI, showMessage, fetchData]);


    const renderDataTable = useMemo(() => {
        if (isLoding || !columns || !lovs || !data || data.length === 0) {
            return null;
        }

        return (
            <DataTable
                showExport={is_admin}
                key={'data_table'}
                getTokenFuncAsync={() => null}
                keyname={'id'}
                data={data}
                columns={columns}
                defaultSortColumn={"id"}
            >
                {is_admin &&
                    <Button
                        id="add-new-button"
                        variant="outlined"
                        color="primary"
                        onClick={() => setShowAdd(true)}
                    >
                        Add
                    </Button>
                }
            </DataTable>
        );
    }, [isLoding, columns, lovs, data, is_admin]);


    const renderEditDialog = useMemo(() => {
        if (!lovs || !editData || !columns || !showEdit || isLoding) {
            return null;
        }
        return (
            <ResistanceDataPageEdit
                columns={columns}
                showEdit={showEdit}
                editData={editData}
                onHandleClose={handleCloseEdit}
                onHandleChangedData={handleChangedData}
                onHandleUpdatePressed={handleUpdate}
                lovs={lovs}
            />
        );
    }, [showEdit, editData, lovs, columns, handleCloseEdit, handleChangedData, handleUpdate, isLoding]);


    const renderNewDialog = useMemo(() => {
        if (!lovs || !columns || !showAdd) {
            return null;
        }
        return (
            <ResistanceDataPageNew
                columns={columns}
                showNew={showAdd}
                onHandleClose={() => setShowAdd(false)}
                onHandleNewPressed={handleNew}
                lovs={lovs}
            />
        );
    }, [showAdd, lovs, columns, handleNew]);


    return (
        <>
            {isLoding && <LinearProgress />}
            {renderDataTable}
            {renderEditDialog}
            {renderNewDialog}
        </>
    );
}